import { useCardContextValue } from '@hts/entities/Card/libs';
import styles from '@hts/entities/Card/styles/InfoContent/TopButtons/UploadLogoImage.module.scss';
import { ILogoImageState } from '@hts/entities/Card/types';
import {
  getCorrectLogoCropData,
  getNewTransformerDataByChangingCrop,
  logoIsEnabledButNotSet,
} from '@hts/entities/Card/utils';
import IconClose from '@hts/oldStructure/components/card-add-image/icon-close';
import IconPencil from '@hts/oldStructure/components/card-add-image/icon-pencil';
import { HtsButton, HtslIcon, HtsTypography } from '@hts/shared/ui';
import { IRect } from 'konva/lib/types';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Cropper, ImageRestriction } from 'react-advanced-cropper';
import { AbstractCropperRef } from 'react-advanced-cropper/dist/components/AbstractCropper';
import useImage from 'use-image';

import '@hts/oldStructure/components/card-add-image/card-add-image.scss';
import 'react-advanced-cropper/dist/style.css';

interface IUploadLogoImageProps {
  isMobile: boolean;
}

export const UploadLogoImage: React.FC<IUploadLogoImageProps> = memo(({ isMobile }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const cropperRef = useRef<AbstractCropperRef>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);

  const card = useCardContextValue('card');
  const setCard = useCardContextValue('setCard');

  const cardLogo = card!.logo!;

  const {
    isDefaultImage,
    width,
    height,
    currentCropWidth,
    currentCropHeight,
    currentCropX,
    currentCropY,
    currentImageUrl,
  } = cardLogo;

  const [showCropPopup, setShowCropPopup] = useState(false);
  const [newImagePath, setNewImagePath] = useState('');
  const [localFile, setLocalFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showUploadPopup, setShowUploadPopup] = useState(false);

  const primaryImage = card?.previewUrl?.image || undefined;
  const [img, imageStatus] = useImage(newImagePath);

  const { cropX, cropY, cropWidth, cropHeight } = getCorrectLogoCropData({
    image: img,
    primaryImage,
    currentCropWidth,
    currentCropHeight,
    currentCropX,
    currentCropY,
    rectWidth: width || 0,
    rectHeight: height || 0,
    isEnabledButNotSet: logoIsEnabledButNotSet(cardLogo),
  });

  useEffect(() => {
    return () => {
      if (newImagePath) {
        URL.revokeObjectURL(newImagePath);
      }
    };
  }, [newImagePath]);

  const cropPopupOpen = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setShowCropPopup(true);
    setShowUploadPopup(false);
    setError(null);
  }, []);

  const cropPopupClose = useCallback(() => {
    setShowCropPopup(false);
    setError(null);
    setNewImagePath('');

    if (fileRef.current) {
      fileRef.current.value = '';
    }
  }, []);

  const onClickUploadImageButton = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      if (currentImageUrl) {
        setShowCropPopup(true);
        setNewImagePath(currentImageUrl);
      } else {
        setShowUploadPopup(true);
      }

      setError(null);
    },
    [card, currentImageUrl]
  );

  const uploadPopupClose = useCallback(() => {
    setShowUploadPopup(false);
    setError(null);
  }, []);

  const onFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const et = e.target;
    const files = et.files;

    const maxSize = 20971520;

    if (files && files[0].size > maxSize) {
      setError(`The image may not be greater than ${maxSize / 1024 / 1024} Mb`);

      return;
    }

    setError(null);

    const reader = new FileReader();

    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        setNewImagePath(reader.result);
      }
    };

    if (files && files[0]) {
      const file = files[0];

      setLocalFile(file);
      reader.readAsDataURL(file);
    }
  }, []);

  const onChangeImage = useCallback(
    (url: string, rect: IRect) => {
      if (!primaryImage) return;

      setCard((prev) => {
        if (!prev) return prev;

        if (!prev.logo) return prev;

        const prevLogo = prev.logo;

        const newLogoData: Partial<ILogoImageState> = {
          ...prevLogo,
          currentImageUrl: url,
          currentCropWidth: rect.width,
          currentCropHeight: rect.height,
          currentCropX: rect.x,
          currentCropY: rect.y,
          isDefaultImage: false,
          clientFileImage: localFile,
        };

        const localIsEnabledButNotSet = logoIsEnabledButNotSet(prevLogo);

        if (localIsEnabledButNotSet) {
          const rectWidth = rect.width;
          const rectHeight = rect.height;

          const newX = primaryImage.width / 2 - rectWidth / 2;
          const newY = primaryImage.height / 2 - rectHeight / 2;

          newLogoData.width = rectWidth;
          newLogoData.height = rectHeight;
          newLogoData.x = Math.round(newX);
          newLogoData.y = Math.round(newY);
          newLogoData.rotation = 0;
        } else {
          const prevX = prevLogo.x || 0;
          const prevY = prevLogo.y || 0;
          const prevWidth = prevLogo.width || 0;
          const prevHeight = prevLogo.height || 0;
          const prevRotation = prevLogo.rotation || 0;
          const prevCropX = prevLogo.currentCropX;
          const prevCropY = prevLogo.currentCropY;
          const prevCropWidth = prevLogo.currentCropWidth;
          const prevCropHeight = prevLogo.currentCropHeight;

          const {
            width: newWidth,
            height: newHeight,
            x: newX,
            y: newY,
          } = getNewTransformerDataByChangingCrop({
            newCrop: rect,
            prevCrop: { x: prevCropX, y: prevCropY, width: prevCropWidth, height: prevCropHeight },

            prevTransformer: {
              x: prevX,
              y: prevY,
              width: prevWidth,
              height: prevHeight,
              rotation: prevRotation,
            },
          });

          newLogoData.width = newWidth;
          newLogoData.height = newHeight;
          newLogoData.x = newX;
          newLogoData.y = newY;
        }

        return {
          ...prev,
          logo: { ...prevLogo, ...newLogoData },
        };
      });
    },
    [localFile, primaryImage]
  );

  const onResult = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      e.preventDefault();

      if (!cropperRef.current) return;

      const coordinates = cropperRef.current.getCoordinates();

      if (coordinates) {
        const rectCoordinates: IRect = {
          width: Math.round(coordinates.width),
          height: Math.round(coordinates.height),
          x: Math.round(coordinates.left),
          y: Math.round(coordinates.top),
        };

        onChangeImage(newImagePath, rectCoordinates);
      }

      cropPopupClose();
    },
    [cropPopupClose, newImagePath, onChangeImage]
  );

  const handleRemoveImage = useCallback((e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setCard((prev) => {
      if (!prev) return prev;

      const prevLogo = prev.logo;
      const isEnabledButNotSet = prevLogo?.isEnabledButNotSet;

      return {
        ...prev,
        logo: prevLogo
          ? {
              ...prevLogo,
              currentImageUrl: prevLogo.image?.fileUrl || '',
              currentCropWidth: prevLogo.crop_width || 0,
              currentCropHeight: prevLogo.crop_height || 0,
              currentCropX: prevLogo.crop_x || 0,
              currentCropY: prevLogo.crop_y || 0,
              width: isEnabledButNotSet ? null : prevLogo.width,
              height: isEnabledButNotSet ? null : prevLogo.height,
              x: isEnabledButNotSet ? null : prevLogo.x,
              y: isEnabledButNotSet ? null : prevLogo.y,
              isDefaultImage: true,
            }
          : prevLogo,
      };
    });
  }, []);

  return (
    <div className={styles.container}>
      <HtsButton
        label="Add logo"
        variant="outlined"
        fullWidth={isMobile}
        style={{
          gap: isDefaultImage ? 20 : 14,
          maxWidth: isMobile ? 320 : 'auto',
          alignSelf: isMobile ? 'center' : 'unset',
        }}
        className={`${styles.uploadLogoButton} ${isDefaultImage ? styles.defaultImageButton : ''}`}
        typographyProps={{ fontWeight: '500', variant: 'body2' }}
        rightContent={
          !isDefaultImage ? (
            <span
              title="Remove current image"
              style={{ backgroundImage: `url(${currentImageUrl})` }}
              className={styles.removeCurrentImage}
              onClick={handleRemoveImage}
            >
              <HtslIcon icon="btn-add-remove" color="white" />
            </span>
          ) : (
            <HtslIcon icon="upload2" color="black" />
          )
        }
        onClick={onClickUploadImageButton}
      />

      <div
        className="image-holder popup-holder"
        style={{ padding: !showUploadPopup ? 'unset' : 'inherit' }}
      >
        <input type="text" className="hide-input cropped-image" name="cropped-image" />

        <div className={showUploadPopup ? 'image-upload-popup active' : 'image-upload-popup'}>
          <div className="popup-content">
            <div className="popup-close" onClick={uploadPopupClose}>
              <IconClose />
            </div>

            <h2>Upload Your Image</h2>

            <p>
              Note! Images should be a <b>jpeg</b> or <b>png</b> file. To convert your <b>PDF</b> to
              jpeg{' '}
              <a href="https://www.freepdfconvert.com/pdf-to-jpg" target="_blank">
                click here.
              </a>
            </p>

            <div className="upload-area">
              <HtsButton
                label="UPLOAD LOGO"
                variant="outlined"
                rightContent={<IconPencil />}
                onClick={cropPopupOpen}
              />
            </div>
          </div>
        </div>

        <div className={showCropPopup ? 'image-upload-popup active second' : 'image-upload-popup'}>
          <div className="popup-content">
            <div className="popup-close" onClick={cropPopupClose}>
              <IconClose />
            </div>

            <h2>Crop Your Image</h2>

            <div className="croppie-holder">
              <span className="jcf-file">
                <span className="jcf-fake-input">No file chosen</span>
                <span className="jcf-upload-button">
                  <span className="jcf-button-content">Upload new image</span>
                </span>

                <input
                  name="innerimage"
                  type="file"
                  className="upload jcf-real-element"
                  accept=".jpg, .jpeg, .png"
                  style={{ position: 'absolute', opacity: '0' }}
                  ref={fileRef}
                  onChange={onFileUpload}
                />
              </span>

              {Boolean(error) && <div className="error">{error}</div>}

              <div className="croppie-msg" ref={cropContainerRef}>
                {!newImagePath && <HtsTypography variant="body2">No image chosen</HtsTypography>}

                {newImagePath && (
                  <>
                    {imageStatus === 'loaded' ? (
                      <Cropper
                        ref={cropperRef}
                        src={img?.src}
                        imageRestriction={ImageRestriction.none}
                        defaultPosition={{ left: cropX, top: cropY }}
                        defaultSize={{ width: cropWidth, height: cropHeight }}
                        canvas={true}
                      />
                    ) : (
                      <div className="loader"></div>
                    )}
                  </>
                )}
              </div>

              <input
                type="button"
                className="apply btn btn-sm"
                disabled={!img}
                value="ADD IMAGE"
                onClick={onResult}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
